import {LinkTarget} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/link_target.types';
import {MissionDetail} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/mission.types';
import {PromotionTracker} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/tracker.types';
import {MissionsSection as MissionsSectionType} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation_page/sections.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  originClick,
  TrackingPromotionCreative,
} from '@aboutyou/legacy-tracking-sdk';
import {useHistory} from '@aboutyou/router';
import {
  TrackPromotions,
  useTrackComponent,
  useTrackOriginClick,
} from '@aboutyou/tracking';
import {
  FONT_SIZES,
  FONT_WEIGHTS,
  mobileOnlyLinaria,
  RowSection,
  SPACING,
  tabletAndUpLinaria,
  Text,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {PropsWithChildren} from 'react';
import {CoinsMission} from '../../../../features/Coins/CoinsWallet/CoinsWalletContent/CoinsMissions/CoinsMission';
import {useWalletStateManager} from '../../../Wallet/utils/useWalletStateManager';

const MAX_MISSIONS = 3;

export type MissionsSectionProps = MissionsSectionType & {
  streamPosition: number;
};

/**
 * Shared behaviour of the Browse missions section variants: the visible
 * missions, navigation to a mission target and opening the coins wallet,
 * both attributed to the section's promotion tracker.
 */
export const useMissionsSection = ({
  missions,
  promotionTracker,
}: Pick<MissionsSectionProps, 'missions' | 'promotionTracker'>) => {
  const history = useHistory();
  const {open: openWallet} = useWalletStateManager();
  const trackOriginClick = useTrackOriginClick();
  const trackComponent = useTrackComponent();

  const trackClickOrigin = () => {
    if (promotionTracker.locationId) {
      trackOriginClick(promotionTracker.locationId as originClick)();
    }
  };

  const navigateToMission = (target: LinkTarget) => {
    trackClickOrigin();

    if (target.external) {
      window.open(target.url, '_blank', 'noopener,noreferrer');
      return;
    }

    // Internal mission targets can come as absolute urls although link targets
    // should be paths, the host differs on feature environments.
    const {pathname, search, hash} = new URL(
      target.url,
      window.location.origin,
    );
    history.push(`${pathname}${search}${hash}`);
  };

  const openCoinsWallet = () => {
    trackClickOrigin();
    trackComponent({
      action: 'click',
      content_type: 'button',
      name: 'see_all_missions_button',
      object_type: 'see_all_missions_button',
    });
    openWallet('coins');
  };

  return {
    visibleMissions: missions.slice(0, MAX_MISSIONS),
    navigateToMission,
    openCoinsWallet,
  };
};

export const MissionsSectionLayout: React.FC<
  PropsWithChildren<{
    promotionTracker: PromotionTracker;
    streamPosition: number;
  }>
> = ({promotionTracker, streamPosition, children}) => (
  <RowSection data-testid="missionsSection">
    <Content
      promotion={{
        source: TrackingPromotionCreative.GenericPromotion,
        promotionTracker,
      }}
      streamPosition={streamPosition}
      triggerOnce
    >
      {children}
    </Content>
  </RowSection>
);

export const MissionsSectionHeader: React.FC<
  PropsWithChildren<Pick<MissionsSectionProps, 'headline' | 'subline'>>
> = ({headline, subline, children}) => (
  <Header>
    <HeaderText>
      <Headline
        data-testid="missionsSectionHeadline"
        size="px12"
        weight="w500"
        transform="uppercase"
        color="Text.Grey"
      >
        {headline}
      </Headline>
      <Subline data-testid="missionsSectionSubline" weight="w400">
        {subline}
      </Subline>
    </HeaderText>
    {children}
  </Header>
);

/**
 * A wallet mission tile that navigates to the mission's target instead of
 * opening the bottom sheet, which is not used on Browse.
 */
export const MissionTile: React.FC<{
  mission: MissionDetail;
  onNavigate: (target: LinkTarget) => void;
}> = ({mission, onNavigate}) => {
  const {t} = useTranslation();
  const target = mission.bottomSheet?.cta?.linkTarget;
  const navigationLabel = t('Go to mission {title}', {
    title: mission.summary.title,
  });

  return (
    <CoinsMission
      data-testid="missionsSectionMission"
      {...mission}
      aria-label={target ? navigationLabel : undefined}
      bottomSheet={undefined}
      onClick={target ? () => onNavigate(target) : undefined}
    />
  );
};

const Content = styled(TrackPromotions)`
  display: flex;
  flex-direction: column;
  gap: ${SPACING['Y.Lg']};

  ${mobileOnlyLinaria(`padding-inline: ${SPACING['X.Lg']};`)}

  ${tabletAndUpLinaria(`gap: ${SPACING['Y.Xl']};`)}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING['X.Xl']};
  padding-top: ${SPACING['Y.Md']};

  ${tabletAndUpLinaria(`
    align-items: flex-end;
    padding-top: 0;
  `)}
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  ${tabletAndUpLinaria(`gap: ${SPACING['Y.Md']};`)}
`;

const Headline = styled(Text)`
  letter-spacing: 0.04em;
  line-height: 1.5;
`;

const Subline = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${FONT_SIZES.px18};
  line-height: 1.45;

  ${tabletAndUpLinaria(`
    font-size: ${FONT_SIZES.px32};
    font-weight: ${FONT_WEIGHTS.w700};
    line-height: 1.25;
  `)}
`;
